// Banner discreto para instalar la PWA. Solo se muestra dentro del módulo Guías.
// Auto-oculta a los 30s. Si el usuario lo descarta, no aparece por 7 días.
import { useEffect, useState } from 'react';
import { Download, X, Bell } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'guias_pwa_banner_dismissed_at';
const DISMISS_DAYS = 7;
const AUTO_HIDE_MS = 30000;

const isStandalone = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator && window.navigator.standalone === true);
};

const wasRecentlyDismissed = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        if (!ts) return false;
        const diffDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
        return diffDays < DISMISS_DAYS;
    } catch {
        return false;
    }
};

export default function PWAInstallBanner({ appName = 'Guías de despacho' }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (isStandalone()) return; // ya instalado
        if (wasRecentlyDismissed()) return;
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setVisible(true);
            // Auto-ocultar a los 30s (sin marcar como dismissed para que reaparezca al recargar)
            const t = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
            return () => clearTimeout(t);
        };
        window.addEventListener('beforeinstallprompt', handler);
        const onInstalled = () => {
            setVisible(false);
            setDeferredPrompt(null);
        };
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setVisible(false);
            setDeferredPrompt(null);
        }
    };

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, Date.now().toString()); } catch {}
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 bg-card border-2 border-primary/40 rounded-lg shadow-lg p-4 animate-in slide-in-from-bottom-4"
            data-testid="pwa-install-banner"
        >
            <div className="flex items-start gap-3">
                <div className="bg-primary/10 rounded-lg p-2 shrink-0">
                    <Bell className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm leading-tight">Instalar {appName}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Acceso rápido desde tu pantalla de inicio y notificaciones en tiempo real del flujo de guías.
                    </p>
                    <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={handleInstall} data-testid="btn-pwa-install">
                            <Download className="h-3.5 w-3.5 mr-1" /> Instalar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={dismiss}>
                            Ahora no
                        </Button>
                    </div>
                </div>
                <button
                    onClick={dismiss}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Cerrar"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
